// --------------------------------------------------------------
// Created On: 2023-02-21
// Author: Zachary Thomas
//
// Last Modified: 2024-02-08
// Modified By: Zachary Thomas
//
// Copyright 2023 - 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, Fragment } from "react";
import useApi from "../../../hooks/useApi";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import SaveChangesModal from "../../../components/SaveChangesModal/SaveChangesModal";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import Error from "../../../components/Error/Error";
import Spinner from "../../../components/Spinner/Spinner";
import PropTypes from "prop-types";
import ViewAttributeForm from "./ViewAttributeForm/ViewAttributeForm";
import { useSelector } from "react-redux";
import { getCurrentUser } from "../../../redux/selectors";
import deepCopy from "../../../utilities/deepCopy";
import IconTooltip from "../../../components/IconTooltip/IconTooltip";
import apiRequest from "../../../utilities/api/apiRequest";
import generateErrorMessage from "../../../utilities/api/generateErrorMessage";
import { API, MAX_VIEW_NAME_LENGTH, MAX_VIEW_ATTRIBUTES } from "../../../constants/miscellaneous";
import { VIEW_TYPES } from "../../../constants/reducerActions";
import userHasPermission from "../../../utilities/userHasPermission";
import {
  CREATE_GLOBAL_VIEWS_PERMISSION,
  DELETE_GLOBAL_VIEWS_PERMISSION,
  UPDATE_GLOBAL_VIEWS_PERMISSION,
} from "../../../constants/permissions";
import styles from "./ViewModal.module.scss";

// Modal for creating, updating, or deleting a view.
export default function ViewModal(props: Props): Component {
  const initialView = {
    viewId: 0,
    name: "",
    shared: false,
    attributes: [],
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const [previousView, setPreviousView] = useState<View>(initialView);
  const [name, setName] = useState<string>("");
  const [shared, setShared] = useState<boolean>(false);
  const [attributes, setAttributes] = useState<FormAttribute[]>([]);
  const currentUser = useSelector(getCurrentUser);

  // Get detailed view information from the API.
  useApi(
    () => {
      setLoading(props.viewId > 0);
      return props.viewId > 0;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/view/${props.viewId}`,
    },
    async (response: Response, responseBody: GetResponseBody) => {
      if (response.ok && responseBody) {
        const view = {
          viewId: responseBody.viewId,
          name: responseBody.name,
          shared: responseBody.shared,
          attributes: responseBody.attributes,
        };
        responseBody.attributes.forEach((attribute, i) => {
          attribute.formId = i + 1;
          attribute.highlightingRules.forEach((highlightingRule, j) => {
            highlightingRule.formId = j + 1;
          });
        });
        setPreviousView(view);
        setName(responseBody.name);
        setShared(responseBody.shared);
        setAttributes(responseBody.attributes);
        setErrorMessage("");
      } else {
        setErrorMessage("Internal server error. Unable to get view settings.");
      }
      setLoading(false);
    },
    [props.viewId]
  );

  // Exit modal if no changes have been made. Otherwise prompt the user.
  function exitModal() {
    // Get comparable current and previous attributes.
    const flatAttributes: FlatAttribute[] = [];
    attributes.forEach((attribute) => {
      const attributeId = attribute.attributeId;
      attribute.highlightingRules.forEach((highlightingRule) => {
        const flatAttribute = {
          attributeId: attributeId,
          comparator: highlightingRule.comparator,
          valueType: highlightingRule.valueType,
          value: highlightingRule.value,
          colorHexCode: highlightingRule.colorHexCode,
        };
        flatAttributes.push(flatAttribute);
      });
    });

    const previousFlatAttributes: FlatAttribute[] = [];
    if (previousView.attributes !== undefined) {
      previousView.attributes.forEach((attribute) => {
        const attributeId = attribute.attributeId;
        attribute.highlightingRules.forEach((highlightingRule) => {
          const flatAttribute = {
            attributeId: attributeId,
            comparator: highlightingRule.comparator,
            valueType: highlightingRule.valueType,
            value: highlightingRule.value,
            colorHexCode: highlightingRule.colorHexCode,
          };
          previousFlatAttributes.push(flatAttribute);
        });
      });
    }

    // Check to see if anything has changed in the view settings.
    if (
      name === previousView.name &&
      shared === previousView.shared &&
      JSON.stringify(flatAttributes) === JSON.stringify(previousFlatAttributes)
    ) {
      // Since there have been no changes we can safely exit.
      discardChanges();
    } else {
      // We have unsaved changes, give the user a chance to save them.
      setShowConfirmExit(true);
    }
  }

  // Save changes.
  function saveChanges() {
    setShowConfirmExit(false);
    setErrorMessage("");
  }

  // Exit without saving changes.
  function discardChanges() {
    props.onClose();
    setShowConfirmDelete(false);
    setShowConfirmExit(false);
    setErrorMessage("");
  }

  // Add an empty attribute.
  function addAttribute() {
    const attributesDeepCopy = deepCopy(attributes);
    let formId = 1;
    attributesDeepCopy.forEach((attribute) => {
      if (attribute.formId >= formId) {
        formId = attribute.formId + 1;
      }
    });
    const attribute = {
      formId: formId,
      attributeId: 0,
      name: "",
      highlightingRules: [],
    };
    attributesDeepCopy.push(attribute);
    setAttributes(attributesDeepCopy);
  }

  // Update an attribute.
  function updateAttribute(formId: number, name: string): void {
    const attributesDeepCopy = deepCopy(attributes);
    const attribute = attributesDeepCopy.find((attribute) => attribute.formId === formId);
    if (attribute !== undefined) {
      const lookUpAttribute = props.attributes.find((lookUpAttribute) => lookUpAttribute.name === name);
      if (lookUpAttribute !== undefined) {
        attribute.attributeId = lookUpAttribute.attributeId;
      } else {
        attribute.attributeId = 0;
      }
      attribute.name = name;
      setAttributes(attributesDeepCopy);
    }
  }

  // Delete an attribute.
  function deleteAttribute(formId: number): void {
    const attributesDeepCopy = deepCopy(attributes);
    const index = attributesDeepCopy.findIndex((attribute) => attribute.formId === formId);
    if (index >= 0) {
      attributesDeepCopy.splice(index, 1);
      setAttributes(attributesDeepCopy);
    }
  }

  // Add a highlighting rule to an attribute.
  function addRule(attributeFormId: number): void {
    const attributesDeepCopy = deepCopy(attributes);
    const attribute = attributesDeepCopy.find((attribute) => attribute.formId === attributeFormId);

    if (attribute !== undefined) {
      // Get a new form ID.
      let formId = 1;
      attribute.highlightingRules.forEach((highlightingRule) => {
        if (highlightingRule.formId >= formId) {
          formId = highlightingRule.formId + 1;
        }
      });

      // Create the initial highlight rule.
      const newHighlightingRule: HighlightingRule = {
        formId: formId,
        comparator: "=",
        valueType: "NUMBER",
        value: "0",
        colorHexCode: "#ffb900",
      };

      attribute.highlightingRules.push(newHighlightingRule);
      setAttributes(attributesDeepCopy);
    }
  }

  // Update a highlighting rule in an attribute.
  function updateRule(
    attributeFormId: number,
    highlightingRuleFormId: number,
    comparator: ViewComparator,
    valueType: ViewValueType,
    value: string,
    colorHexCode: string
  ): void {
    const attributesDeepCopy = deepCopy(attributes);
    const attribute = attributesDeepCopy.find((attribute) => attribute.formId === attributeFormId);

    if (attribute !== undefined) {
      const highlightingRuleIndex = attribute.highlightingRules.findIndex(
        (highlightingRule) => highlightingRule.formId === highlightingRuleFormId
      );
      if (highlightingRuleIndex >= 0) {
        const updatedHighlightingRule: HighlightingRule = {
          formId: highlightingRuleFormId,
          comparator: comparator,
          valueType: valueType,
          value: value,
          colorHexCode: colorHexCode,
        };

        // Before updating the highlighting rule, makes sure that based on the value type, specific settings are valid.
        // For each case remove secondary values if they aren't used, and set invalid comparators to a default value.
        switch (valueType) {
          case "NUMBER":
            if (comparator !== "<>") {
              updatedHighlightingRule.value = value.split(",")[0];
            }
            break;
          case "MAX":
            updatedHighlightingRule.comparator = "=";
            updatedHighlightingRule.value = value.split(",")[0];
            break;
          case "MIN":
            updatedHighlightingRule.comparator = "=";
            updatedHighlightingRule.value = value.split(",")[0];
            break;
          case "MEDIAN":
            if (comparator === "<>") {
              updatedHighlightingRule.comparator = ">";
            }
            updatedHighlightingRule.value = value.split(",")[0];
            break;
          case "MEAN":
            if (comparator === "<>") {
              updatedHighlightingRule.comparator = ">";
            }
            updatedHighlightingRule.value = value.split(",")[0];
            break;
          case "STANDARD_DEVIATION":
            if (comparator === "<>" || comparator === "=") {
              updatedHighlightingRule.comparator = ">";
            }
            if (!["1", "2", "3"].includes(value)) {
              updatedHighlightingRule.value = "1";
            }
            break;
        }

        attribute.highlightingRules.splice(highlightingRuleIndex, 1, updatedHighlightingRule);
        setAttributes(attributesDeepCopy);
      }
    }
  }

  // Delete a highlighting rule from an attribute.
  function deleteRule(attributeFormId: number, highlightingRuleFormId: number): void {
    const attributesDeepCopy = deepCopy(attributes);
    const attribute = attributesDeepCopy.find((attribute) => attribute.formId === attributeFormId);

    if (attribute !== undefined) {
      const highlightingRuleIndex = attribute.highlightingRules.findIndex(
        (highlightingRule) => highlightingRule.formId === highlightingRuleFormId
      );
      if (highlightingRuleIndex >= 0) {
        attribute.highlightingRules.splice(highlightingRuleIndex, 1);
        setAttributes(attributesDeepCopy);
      }
    }
  }

  // Check if the current view is valid.
  function viewIsValid(): boolean {
    // Check each element of arrays to see if an attribute was not correctly entered by the user.
    let invalidAttributeIndex = -1;
    let ruleIsInvalid = false;
    let ruleIsBetweenTwoValues = true;
    attributes.forEach((attribute, i) => {
      if (attribute.attributeId === 0) {
        invalidAttributeIndex = i;
      }

      // Check each highlighting rule to make sure they are valid.
      attribute.highlightingRules.forEach((highlightingRule) => {
        let valueIsInvalid = false;
        if (highlightingRule.valueType === "NUMBER") {
          if (highlightingRule.comparator === "<>") {
            const values = highlightingRule.value.split(",");
            const primaryValue = parseFloat(values[0]);
            const secondaryValue = parseFloat(values[1]);
            // If we are checking 'between two values', make sure both values are valid numbers.
            if (!isNaN(primaryValue) && !isNaN(secondaryValue)) {
              // Make sure that the order of the values is correct.
              // For 'between two values' the primary value should be smaller than the secondary value.
              if (primaryValue >= secondaryValue) {
                invalidAttributeIndex = i;
                ruleIsBetweenTwoValues = false;
              }
            } else {
              valueIsInvalid = true;
            }
          } else if (isNaN(parseFloat(highlightingRule.value))) {
            valueIsInvalid = true;
          }
        }
        if (valueIsInvalid) {
          invalidAttributeIndex = i;
          ruleIsInvalid = true;
        }
      });
    });

    if (name.length === 0) {
      setErrorMessage("A view is required to have a name.");
      return false;
    } else if (attributes.length === 0) {
      setErrorMessage("A view must include at least one attribute.");
      return false;
    } else if (invalidAttributeIndex >= 0 && !ruleIsBetweenTwoValues) {
      setErrorMessage(
        `Attribute #${
          invalidAttributeIndex + 1
        } has an invalid highlighting rule. When highlighting between two numbers, the first number must be smaller than the second number.`
      );
      return false;
    } else if (invalidAttributeIndex >= 0 && ruleIsInvalid) {
      setErrorMessage(`Attribute #${invalidAttributeIndex + 1} has an invalid highlighting rule.`);
      return false;
    } else if (invalidAttributeIndex >= 0 && !ruleIsInvalid) {
      setErrorMessage(`Attribute #${invalidAttributeIndex + 1} has an invalid attribute name.`);
      return false;
    } else {
      return true;
    }
  }

  // Create a view.
  async function createView(): Promise<void> {
    if (viewIsValid()) {
      const requestBody = {
        name: name,
        shared: shared,
        attributes: attributes,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/view`,
        "POST",
        requestBody
      )) as [Response, PostResponseBody];
      setLoading(false);

      if (response.ok) {
        const viewDeepCopy = deepCopy(previousView);
        viewDeepCopy.viewId = responseBody.viewId;
        viewDeepCopy.name = name;
        viewDeepCopy.shared = shared;
        setPreviousView(viewDeepCopy);
        props.onAction({
          type: VIEW_TYPES.CREATE_VIEW,
          payload: {
            view: viewDeepCopy,
          },
        });
        setShowConfirmExit(false);
        setErrorMessage("");
        props.onClose();
      } else {
        setErrorMessage(generateErrorMessage(responseBody, response.status, "Unable to create view."));
      }
    }
  }

  // Edit a view.
  async function editView(viewId: number): Promise<void> {
    if (viewIsValid()) {
      const requestBody = {
        name: name,
        shared: shared,
        attributes: attributes,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/view/${viewId}`,
        "PUT",
        requestBody
      )) as [Response, PutResponseBody];
      setLoading(false);

      if (response.ok) {
        const viewDeepCopy = deepCopy(previousView);
        viewDeepCopy.viewId = viewId;
        viewDeepCopy.name = name;
        viewDeepCopy.shared = shared;
        setPreviousView(viewDeepCopy);
        props.onAction({
          type: VIEW_TYPES.UPDATE_VIEW,
          payload: {
            view: viewDeepCopy,
          },
        });
        setErrorMessage("");
        props.onClose();
      } else {
        setErrorMessage(generateErrorMessage(responseBody, response.status, "Unable to update view."));
      }
    }
  }

  // Delete a view.
  async function deleteView(viewId: number): Promise<void> {
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/view/${viewId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      discardChanges();
      props.onAction({
        type: VIEW_TYPES.DELETE_VIEW,
        payload: {
          viewId: viewId,
        },
      });
    } else {
      setShowConfirmDelete(false);
      setErrorMessage(generateErrorMessage(responseBody, response.status, "Unable to delete view."));
    }
  }

  // Returns whether the current view is allowed to be set to 'shared' with the user's current permissions.
  function viewIsSharable(): boolean {
    return (
      (props.isCreatingNewRecord && userHasPermission([[CREATE_GLOBAL_VIEWS_PERMISSION]])) ||
      (!props.isCreatingNewRecord && previousView.shared && userHasPermission([[UPDATE_GLOBAL_VIEWS_PERMISSION]])) ||
      (!props.isCreatingNewRecord && !previousView.shared && userHasPermission([[CREATE_GLOBAL_VIEWS_PERMISSION]]))
    );
  }

  // Returns whether the current user should be able to edit this view in general.
  function viewIsEditable(): boolean {
    return (
      props.isCreatingNewRecord ||
      (!props.isCreatingNewRecord && !previousView.shared) ||
      (!props.isCreatingNewRecord && previousView.shared && userHasPermission([[UPDATE_GLOBAL_VIEWS_PERMISSION]]))
    );
  }

  // Returns whether the current user can delete this view.
  function viewIsDeletable(): boolean {
    return (
      (!props.isCreatingNewRecord && !previousView.shared) ||
      (!props.isCreatingNewRecord && previousView.shared && userHasPermission([[DELETE_GLOBAL_VIEWS_PERMISSION]]))
    );
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={true}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">{props.isCreatingNewRecord ? "Create View" : "Edit View"}</h5>
        </ModalHeader>

        <ModalBody>
          <div className="mx-2 mt-3">
            <label className="mb-3">Name</label>
            <input
              data-test="view-name-input"
              className="form-control mx-auto mb-4"
              type="text"
              maxLength={MAX_VIEW_NAME_LENGTH}
              value={name}
              disabled={!viewIsEditable()}
              onChange={(e) => setName(e.target.value)}
            />

            {/* Toggle sharable views on or off. */}
            {viewIsSharable() && (
              <div className={`${styles.switch} my-4`}>
                <div className="form-check form-switch">
                  <input
                    data-test="view-is-shared-checkbox"
                    className="form-check-input me-3"
                    type="checkbox"
                    checked={shared}
                    onChange={() => setShared((prev) => !prev)}
                  />
                  {shared ? (
                    <label onClick={() => setShared((prev) => !prev)}>Shared view is enabled</label>
                  ) : (
                    <label onClick={() => setShared((prev) => !prev)}>Shared view is disabled</label>
                  )}
                  <IconTooltip
                    id="attribute-tooltip"
                    icon="info-circle"
                    message={`By default, views are only accessible by the user who created them.
                    Shared views can be used by any user in the company.
                    `}
                    color="var(--info-tooltip)"
                  />
                </div>
              </div>
            )}

            {/* Attributes. */}
            <div>
              <div className="mx-1">
                <label className={styles.title}>
                  <span>Attributes&nbsp;</span>
                  <IconTooltip
                    id="attribute-tooltip"
                    icon="info-circle"
                    message={`The attributes selected will determine what data to show for each asset
                    in the view. Attribute values can also be highlighted dynamically based on custom
                    rules. If one value would be highlighted by two separate rules, the rule higher in the list
                    will take priority.
                    `}
                    color="var(--info-tooltip)"
                  />
                </label>

                <button
                  data-test="view-add-attr-button"
                  type="submit"
                  className={`${styles.addButton} btn btn-success float-end`}
                  onClick={() => addAttribute()}
                  disabled={!viewIsEditable() || attributes.length >= MAX_VIEW_ATTRIBUTES}
                >
                  <span className="d-none d-sm-inline">Add Attribute</span>
                  <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
                </button>
              </div>

              {attributes.map((attribute, i) => (
                <ViewAttributeForm
                  key={attribute.formId}
                  formIndex={i}
                  formId={attribute.formId}
                  name={attribute.name}
                  attributes={props.attributes}
                  highlightingRules={attribute.highlightingRules}
                  disabled={!viewIsEditable()}
                  onChangeAttribute={(name) => {
                    updateAttribute(attribute.formId, name);
                  }}
                  onDeleteAttribute={() => deleteAttribute(attribute.formId)}
                  onAddRule={() => addRule(attribute.formId)}
                  onChangeRule={(formId, comparator, valueType, value, colorHexCode) =>
                    updateRule(attribute.formId, formId, comparator, valueType, value, colorHexCode)
                  }
                  onDeleteRule={(formId) => deleteRule(attribute.formId, formId)}
                />
              ))}
            </div>

            {errorMessage.length > 0 && (
              <div className="mt-4">
                <Error message={errorMessage} />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className={styles.footer}>
          {props.isCreatingNewRecord ? (
            <Fragment>
              <button
                data-test="create-view-button"
                className={`${styles.btn} btn btn-primary`}
                type="button"
                onClick={() => createView()}
              >
                Create View
              </button>

              <button
                data-test="cancel-create-view-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Cancel
              </button>
            </Fragment>
          ) : (
            <Fragment>
              {viewIsDeletable() && (
                <button
                  data-test="delete-view-button"
                  className={`${styles.btn} btn btn-danger me-auto`}
                  type="button"
                  onClick={() => setShowConfirmDelete(true)}
                >
                  Delete View
                </button>
              )}

              {viewIsEditable() && (
                <button
                  data-test="save-edit-view-button"
                  className={`${styles.btn} btn btn-primary`}
                  type="button"
                  onClick={() => editView(props.viewId)}
                >
                  Save Changes
                </button>
              )}

              <button
                data-test="cancel-edit-view-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Close
              </button>
            </Fragment>
          )}
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmDelete}
        title={`Delete '${name}'`}
        content={`Are you sure that you want to delete the view '${name}'?`}
        yesText="Delete View"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => deleteView(props.viewId)}
        onNo={() => setShowConfirmDelete(false)}
      />

      <SaveChangesModal
        showModal={showConfirmExit}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmExit(false)}
        onSave={() => saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </div>
  );
}

ViewModal.propTypes = {
  isCreatingNewRecord: PropTypes.bool.isRequired,
  viewId: PropTypes.number.isRequired,
  attributes: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};

interface Props {
  isCreatingNewRecord: boolean;
  viewId: number;
  attributes: ViewAttribute[];
  onClose: () => void;
  onAction: (action: Action) => void;
}

interface View {
  viewId: number;
  name: string;
  shared: boolean;
  attributes: ViewRuleAttribute[];
}

interface ViewRuleAttribute {
  attributeId: number;
  name: string;
  highlightingRules: HighlightingRule[];
}

interface HighlightingRule {
  formId: number;
  comparator: ViewComparator;
  valueType: ViewValueType;
  value: string;
  colorHexCode: string;
}

interface ViewAttribute {
  attributeId: number;
  name: string;
  code: string;
  units: string | null;
}

interface GetResponseBody {
  viewId: number;
  name: string;
  shared: boolean;
  attributes: FormAttribute[];
}

interface PostResponseBody {
  viewId: number;
  error?: string;
}

interface PutResponseBody {
  error?: string;
}

interface DeleteResponseBody {
  error?: string;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  views?: View[];
  view?: View;
  viewId?: number;
}

interface FormAttribute {
  formId: number;
  attributeId: number;
  name: string;
  highlightingRules: HighlightingRule[];
}

interface FlatAttribute {
  attributeId: number;
  comparator: ViewComparator;
  valueType: ViewValueType;
  value: string;
  colorHexCode: string;
}

type ViewValueType = "NUMBER" | "MEDIAN" | "MEAN" | "STANDARD_DEVIATION" | "MAX" | "MIN";

type ViewComparator = "=" | "!=" | "<" | ">" | "<>";
